import type { PropsWithChildren, ReactElement } from 'react';
import React, { createContext, useEffect, useState } from 'react';
import type { Configurations } from "@env/EnvironmentVariables";
import { stripTrailingSlash } from "@env/URLs";
import { Record as ImmutableRecord } from "immutable";

export class BimTrackConfiguration extends ImmutableRecord<Configurations>({
  launchDarklyClientId: "",
  appInsightsConnectionString: "",
  authenticationClientId: "",
  authenticationServerBaseUrl: "",
  bimTrackApiBaseUrl: "",
  fileManagementApiBaseUrl: "",
  fileManagementRegionalUrlTemplate: "",
  subscriptionServiceApi: "",
  scopes: "",
  secondaryViewerCategorySecret: ""
}) {}

declare const __webpack_public_path__: string;
export const BimTrackConfigurationContext = createContext<BimTrackConfiguration>(new BimTrackConfiguration());

type JsonFile = Record<string, string>;

async function fetchFileWithoutPreflightCheck(fileUrl: string): Promise<JsonFile> {
  const response = await fetch(fileUrl, {
    method: "GET",
    headers: {
      'Content-Type': 'text/plain'
    },
  });

  return await response.json();
}

async function fetchBimTrackConfigurations(): Promise<BimTrackConfiguration> {
  const fileManagementConfigurations = await fetchFileWithoutPreflightCheck(`${stripTrailingSlash(__webpack_public_path__)}/configuration.json`);

  return new BimTrackConfiguration(fileManagementConfigurations);
}

export default function BimTrackConfigurationsProvider({ children }: PropsWithChildren<unknown>): ReactElement | null {
  const [configuration, setConfiguration] = useState<BimTrackConfiguration | undefined>(undefined);

  useEffect(() => {
    const controller = new AbortController();

    fetchBimTrackConfigurations()
      .then(received => setConfiguration(received))
      .catch(error => {
        const errorName = error.name;

        if (errorName !== 'AbortError' && errorName !== 'RequestCanceledError') {
          throw error;
        }
      });

    return () => {
      controller.abort();
    };
  }, []);

  if (!configuration) {
    return null;
  }

  return (
    <BimTrackConfigurationContext.Provider value={configuration}>
      {children}
    </BimTrackConfigurationContext.Provider>
  );
}
